import { baseApiClient as api } from "../baseService";

export const addTagTypes = ["firms"] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      filevineOrganizationLinkGet: build.query<
        FilevineOrganizationLinkGetApiResponse,
        FilevineOrganizationLinkGetApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/filevine-organization/get_link/`,
        }),
        providesTags: ["firms"],
      }),
      filevineOrganizationLinkCreate: build.mutation<
        FilevineOrganizationLinkCreateApiResponse,
        FilevineOrganizationLinkCreateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/filevine-organization/link/`,
          method: "POST",
          body: queryArg.linkFirmToOrganizationSchema,
        }),
        invalidatesTags: ["firms"],
      }),
      filevineOrganizationsList: build.query<
        FilevineOrganizationsListApiResponse,
        FilevineOrganizationsListApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/filevine-organization/organizations/`,
        }),
        providesTags: ["firms"],
      }),
      filevineOrganizationUnlink: build.mutation<
        FilevineOrganizationUnlinkApiResponse,
        FilevineOrganizationUnlinkApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/filevine-organization/unlink/`,
          method: "POST",
        }),
        invalidatesTags: ["firms"],
      }),
      filevineProjectConfigsList: build.query<
        FilevineProjectConfigsListApiResponse,
        FilevineProjectConfigsListApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/filevine-project-configs/`,
        }),
        providesTags: ["firms"],
      }),
      filevineProjectConfigSyncTrigger: build.mutation<
        FilevineProjectConfigSyncTriggerApiResponse,
        FilevineProjectConfigSyncTriggerApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/filevine-project-configs/sync/`,
          method: "POST",
          body: queryArg.triggerProjectConfigSyncSchema,
        }),
        invalidatesTags: ["firms"],
      }),
      filevineSectionConfigsList: build.query<
        FilevineSectionConfigsListApiResponse,
        FilevineSectionConfigsListApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/filevine-sections/`,
        }),
        providesTags: ["firms"],
      }),
      filevineSectionConfigRetrieve: build.query<
        FilevineSectionConfigRetrieveApiResponse,
        FilevineSectionConfigRetrieveApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/filevine-sections/${queryArg.id}/`,
        }),
        providesTags: ["firms"],
      }),
      filevineSectionConfigUpdateType: build.mutation<
        FilevineSectionConfigUpdateTypeApiResponse,
        FilevineSectionConfigUpdateTypeApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/filevine-sections/${queryArg.id}/type/`,
          method: "PATCH",
          body: queryArg.patchedUpdateSectionTypeSchema,
        }),
        invalidatesTags: ["firms"],
      }),
    }),
    overrideExisting: false,
  });
export { injectedRtkApi as enhancedApi };
export type FilevineOrganizationLinkGetApiResponse =
  /** status 200  */ FirmFilevineOrganizationLinkSchema;
export type FilevineOrganizationLinkGetApiArg = {
  firmId: string;
};
export type FilevineOrganizationLinkCreateApiResponse =
  /** status 200  */ FirmFilevineOrganizationLinkSchema;
export type FilevineOrganizationLinkCreateApiArg = {
  firmId: string;
  linkFirmToOrganizationSchema: LinkFirmToOrganizationSchema;
};
export type FilevineOrganizationsListApiResponse =
  /** status 200  */ FilevineOrganizationWithLinkStatusSchema;
export type FilevineOrganizationsListApiArg = {
  firmId: string;
};
export type FilevineOrganizationUnlinkApiResponse =
  /** status 200  */ FirmFilevineOrganizationLinkSchema;
export type FilevineOrganizationUnlinkApiArg = {
  firmId: string;
};
export type FilevineProjectConfigsListApiResponse =
  /** status 200  */ FilevineProjectConfigWithSectionsSchema[];
export type FilevineProjectConfigsListApiArg = {
  firmId: string;
};
export type FilevineProjectConfigSyncTriggerApiResponse =
  /** status 200  */ ProjectConfigSyncResultSchema;
export type FilevineProjectConfigSyncTriggerApiArg = {
  firmId: string;
  triggerProjectConfigSyncSchema: TriggerProjectConfigSyncSchema;
};
export type FilevineSectionConfigsListApiResponse =
  /** status 200  */ FilevineSectionConfigDetailedSchema[];
export type FilevineSectionConfigsListApiArg = {
  firmId: string;
};
export type FilevineSectionConfigRetrieveApiResponse =
  /** status 200  */ FilevineSectionConfigDetailedSchema;
export type FilevineSectionConfigRetrieveApiArg = {
  firmId: string;
  id: string;
};
export type FilevineSectionConfigUpdateTypeApiResponse =
  /** status 200  */ FilevineSectionConfigDetailedSchema;
export type FilevineSectionConfigUpdateTypeApiArg = {
  firmId: string;
  id: string;
  patchedUpdateSectionTypeSchema: UpdateSectionTypeSchema;
};
export type FirmFilevineOrganizationLinkSchema = {
  firm_id: string | string;
  firm_name: string;
  filevine_organization_id: (string | string) | null;
  filevine_org_name: string | null;
  /** The Filevine organization ID */
  filevine_id?: number | null;
};
export type LinkFirmToOrganizationSchema = {
  /** The UUID of the FilevineOrganization to link to */
  filevine_organization_id: string | string;
};
export type FilevineOrganizationWithLinkStatusSchema = {
  id: string | string;
  filevine_id: number;
  org_name: string;
  created_at: string;
  modified_at: string;
  /** ID of the firm linked to this organization, if any */
  linked_firm_id?: (string | string) | null;
  /** Name of the firm linked to this organization, if any */
  linked_firm_name?: string | null;
};
export type CanonicalSectionType = "medical_providers" | "coverage";
export type FilevineSectionConfigDetailedSchema = {
  id: string | string;
  project_type_id: string | string;
  filevine_id: number;
  name: string;
  internal_name: string;
  section_type?: CanonicalSectionType;
  data: {
    [key: string]: any;
  };
  created_at: string;
  modified_at: string;
};
export type FilevineProjectConfigWithSectionsSchema = {
  id: string | string;
  organization_id: string | string;
  filevine_id: number;
  name: string;
  data: {
    [key: string]: any;
  };
  created_at: string;
  modified_at: string;
  sections: FilevineSectionConfigDetailedSchema[];
};
export type ProjectConfigSyncResultSchema = {
  project_configs_upserted?: number;
  section_configs_upserted?: number;
  field_configs_upserted?: number;
  organization_id?: (string | string) | null;
  workflow_id: string;
};
export type TriggerProjectConfigSyncSchema = {
  matter_id: string | string;
};
export type UpdateSectionTypeSchema = {
  /** The canonical section type to set (e.g., 'medical_providers') */
  section_type?: CanonicalSectionType;
};
export const {
  useFilevineOrganizationLinkGetQuery,
  useFilevineOrganizationLinkCreateMutation,
  useFilevineOrganizationsListQuery,
  useFilevineOrganizationUnlinkMutation,
  useFilevineProjectConfigsListQuery,
  useFilevineProjectConfigSyncTriggerMutation,
  useFilevineSectionConfigsListQuery,
  useFilevineSectionConfigRetrieveQuery,
  useFilevineSectionConfigUpdateTypeMutation,
} = injectedRtkApi;
